export const FLEET_CHART_DATE_TYPES = [
  { value: 'last5mins', label: 'Last 5 mins', ms: 5 * 60 * 1000 },
  { value: 'last15mins', label: 'Last 15 mins', ms: 15 * 60 * 1000 },
  { value: 'last30mins', label: 'Last 30 mins', ms: 30 * 60 * 1000 },
  { value: 'last1hour', label: 'Last 1 hour', ms: 60 * 60 * 1000 },
  { value: 'last6Hours', label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
  { value: 'last12hours', label: 'Last 12 hours', ms: 12 * 60 * 60 * 1000 },
  { value: 'last1Day', label: 'Last 1 day', ms: 24 * 60 * 60 * 1000 },
  { value: 'last3Days', label: 'Last 3 days', ms: 3 * 24 * 60 * 60 * 1000 },
  { value: 'last7Days', label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { value: 'last14Days', label: 'Last 14 days', ms: 14 * 24 * 60 * 60 * 1000 },
  { value: 'last30Days', label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
]

export const DEFAULT_FLEET_CHART_DATE_TYPE = 'last1hour'

export const FLEET_CHART_SERIES_COLORS = [
  '#ff3925',
  '#009513',
  '#ffa42b',
  '#9b644c',
  '#bf4588',
  '#e6cb00',
  '#0562c0',
  '#f5845a',
  '#4b8b03',
  '#a356a2',
  '#00b7c3',
  '#7c4dff',
  '#c2185b',
  '#43a047',
  '#fb8c00',
  '#5c6bc0',
  '#00897b',
  '#d81b60',
  '#6d4c41',
  '#039be5',
]

/** Gap larger than this fraction of the selected range becomes a line break (NaN). */
const GAP_FRACTION = 0.02
const MIN_GAP_MS = 60 * 1000

export const getDateTypeMs = (dateType) => {
  const found = FLEET_CHART_DATE_TYPES.find((item) => item.value === dateType)
  return found?.ms || 60 * 60 * 1000
}

export const getDateTypeLabel = (dateType) => {
  const found = FLEET_CHART_DATE_TYPES.find((item) => item.value === dateType)
  return found?.label || dateType || ''
}

export const getChartOriginMs = (dateType, now = Date.now()) => now - getDateTypeMs(dateType)

export const colorForUnitIndex = (index) => (
  FLEET_CHART_SERIES_COLORS[index % FLEET_CHART_SERIES_COLORS.length]
)

export const formatChartCursorTime = (ms) => {
  if (ms == null || !Number.isFinite(ms)) return '—'
  const d = new Date(ms)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  let h = d.getHours()
  const m = d.getMinutes()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h}:${String(m).padStart(2, '0')} ${ampm}`
}

export const formatChartAxisDateTime = (ms) => {
  if (ms == null || !Number.isFinite(ms)) return ''
  const d = new Date(ms)
  const M = String(d.getMonth() + 1).padStart(2, '0')
  const D = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${M}/${D}, ${h}:${m}`
}

export const formatChartValue = (value, decimals = 2) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return num.toFixed(decimals)
}

/**
 * Merge incoming points into an existing series, insert NaN breaks for large gaps,
 * and drop duplicate timestamps.
 */
export const mergeChartPoints = (existingPoints = [], incomingPoints = [], dateType) => {
  if (!incomingPoints?.length) return existingPoints || []

  const rangeMs = getDateTypeMs(dateType)
  const gapThreshold = Math.max(MIN_GAP_MS, rangeMs * GAP_FRACTION)

  const byTs = new Map()
  ;(existingPoints || []).forEach((point) => {
    if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
      byTs.set(point.x, point)
    }
  })
  incomingPoints.forEach((point) => {
    const ts = Number(point?.timestamp ?? point?.x)
    const value = point?.value ?? point?.y
    if (!Number.isFinite(ts)) return
    if (value == null || value === '' || !Number.isFinite(Number(value))) return
    byTs.set(ts, { x: ts, y: Number(value) })
  })

  const sorted = [...byTs.values()].sort((a, b) => a.x - b.x)
  if (sorted.length < 2) return sorted

  const withGaps = [sorted[0]]
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    if (curr.x - prev.x > gapThreshold) {
      withGaps.push({ x: prev.x + 1, y: Number.NaN })
    }
    withGaps.push(curr)
  }
  return withGaps
}

/**
 * Normalize FleetChartDataResponse chunk into unit series updates.
 */
export const extractFleetChartUnits = (payload) => {
  if (!payload) return []
  const units = payload.units || payload.Units || payload.data?.units || payload.data
  if (!Array.isArray(units)) return []
  return units.map((unit) => ({
    unitUId: unit.unitUId || unit.UnitUId || unit.uid || '',
    unitName: unit.unitName || unit.UnitName || unit.name || '',
    licenseId: unit.licenseId || unit.LicenseId || '',
    fieldName: unit.fieldName || unit.FieldName || '',
    channelName: unit.channelName || unit.ChannelName || '',
    channelUnit: unit.channelUnit || unit.ChannelUnit || unit.unit || '',
    data: Array.isArray(unit.data) ? unit.data : (Array.isArray(unit.Data) ? unit.Data : []),
  })).filter((unit) => unit.unitUId)
}

/**
 * Collect unique standard field names from live fleet units (dashboard fields preferred).
 */
export const collectStandardChannelOptions = (units = []) => {
  const seen = new Set()
  const options = []

  const pushField = (fieldName, unit) => {
    const name = String(fieldName || '').trim()
    if (!name || seen.has(name)) return
    seen.add(name)
    options.push({ value: name, label: name, unit: unit || '' })
  }

  units.forEach((unit) => {
    const fields = Array.isArray(unit?.fields) ? unit.fields.filter((f) => !f?.isCustom) : []
    const dashboard = fields.filter((f) => f?.isDashboardField && f?.fieldName)
    const source = dashboard.length ? dashboard : fields.filter((f) => f?.fieldName)
    source
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((field) => pushField(field.fieldName, field.unit))
  })

  return options
}

/**
 * Convert absolute-ms points to offset-from-origin for ArctionDashboard.
 */
export const toOriginOffsetPoints = (points, originMs) => {
  if (!points?.length || originMs == null) return []
  return points.map((point) => ({
    x: point.x - originMs,
    y: point.y,
  }))
}
